import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, MoreVertical } from 'lucide-react'
import { Button } from '../ui/button'
import { Avatar } from '../ui/avatar'
import MessageBubble from './MessageBubble'
import MessageInput from './MessageInput'
import EmptyMessagesState from './EmptyMessagesState'
import LoadingConversations from './LoadingConversations'
import ConversationActionsMenu from './ConversationActionsMenu'
import NewMessagesDivider from './NewMessagesDivider'
import useMessages from '../../utilities/hooks/useMessages'
import { messagesChannelManager } from '../../channels/messages_channel'
import useAuth from '../../utilities/hooks/useAuth'

function ConversationView({ conversation, onBack, onArchive, onBlock, onReport, onMessagesRead }) {
  const { currentUser } = useAuth()
  const { messages, isLoading, sendMessage, addMessage, updateMessage } = useMessages(conversation?.id)
  const messagesEndRef = useRef(null)
  const [showActions, setShowActions] = useState(false)

  const otherParticipant = conversation?.attributes?.otherParticipant
  const isBlocked = conversation?.attributes?.isBlocked || false

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (conversation && onMessagesRead) {
      onMessagesRead(conversation.id)
    }
  }, [conversation?.id])

  useEffect(() => {
    if (!conversation) return

    const handleChannelMessage = (data) => {
      if (data.type === 'new_message' && data.conversation_id === conversation.id) {
        addMessage(data.message)
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      } else if (data.type === 'message_read' && data.conversation_id === conversation.id) {
        updateMessage(data.message_id, { read_at: new Date().toISOString() })
      }
    }

    messagesChannelManager.addCallback(handleChannelMessage)

    return () => {
      messagesChannelManager.removeCallback(handleChannelMessage)
    }
  }, [conversation, addMessage, updateMessage])

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8 text-center">
        <p className="text-gray-500">Select a conversation to start messaging</p>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingConversations />
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
        <Button onClick={onBack} variant="ghost" size="icon" className="lg:hidden">
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <Avatar className="w-10 h-10">
          {otherParticipant?.cloudinaryPhotoPublicId ? (
            <img
              src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_40,w_40/${otherParticipant.cloudinaryPhotoPublicId}.jpg`}
              alt={otherParticipant.preferredName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold">
              {(otherParticipant?.preferredName || '?')[0].toUpperCase()}
            </div>
          )}
        </Avatar>

        <div className="flex-1 min-w-0">
          <h2 className={`font-semibold truncate ${isBlocked ? 'text-red-700' : 'text-gray-900'}`}>
            {otherParticipant?.preferredName || otherParticipant?.fullName}
            {isBlocked && <span className="ml-2 text-xs font-normal text-red-600">(Blocked)</span>}
          </h2>
        </div>

        <div className="relative">
          <Button onClick={() => setShowActions(!showActions)} variant="ghost" size="icon">
            <MoreVertical className="w-5 h-5" />
          </Button>
          {showActions && (
            <ConversationActionsMenu
              onArchive={() => {
                onArchive(conversation.id)
                setShowActions(false)
              }}
              onBlock={() => {
                onBlock(otherParticipant?.id)
                setShowActions(false)
              }}
              onReport={() => {
                onReport(conversation.id)
                setShowActions(false)
              }}
              onClose={() => setShowActions(false)}
            />
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {isBlocked && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-sm text-red-800">
              This conversation is with a blocked user. You cannot send or receive new messages.
            </p>
          </div>
        )}
        {messages.length === 0 ? (
          <EmptyMessagesState />
        ) : (
          <div className="flex flex-col-reverse gap-1">
            <div ref={messagesEndRef} />
            {messages.map((message, index) => {
              const senderId = message.attributes?.sender?.id
              const currentUserId = currentUser?.id
              const isOwn = senderId && currentUserId && senderId.toString() === currentUserId.toString()
              const readAt = message.attributes?.readAt || message.readAt
              const isUnread = !isOwn && !readAt

              const nextMessage = messages[index + 1]
              const nextReadAt = nextMessage?.attributes?.readAt || nextMessage?.readAt
              const nextIsOwn =
                nextMessage?.attributes?.sender?.id &&
                currentUserId &&
                nextMessage.attributes.sender.id.toString() === currentUserId.toString()
              const showDivider = isUnread && (nextReadAt || nextIsOwn || !nextMessage)

              return (
                <React.Fragment key={message.id}>
                  <MessageBubble message={message} isOwn={isOwn} />
                  {showDivider && <NewMessagesDivider />}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>

      <MessageInput conversationId={conversation.id} onSendMessage={sendMessage} disabled={isBlocked} />
    </div>
  )
}

ConversationView.propTypes = {
  conversation: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onBlock: PropTypes.func.isRequired,
  onReport: PropTypes.func.isRequired,
  onMessagesRead: PropTypes.func
}

export default ConversationView
